import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import autoTable from 'jspdf-autotable';
import jsPDF from "jspdf";
import moment from "moment";
import React, { useState, useEffect } from 'react'

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";

import { callAddFontNirmalaBold } from '../../../styles/fonts/Nirmala-bold';
import { callAddFontNirmalaNormal } from '../../../styles/fonts/Nirmala-normal';
import { excelConfig, filterHeaderXlStyle, filterHeaderXlDataStyle } from '../../../configs/excelConfig';
import { fetchApi } from '../../../services/apiService';
import { hasAccess, NumberFormatter, jsPdfHeaderFooter, jsPdfDidParseCellCallback, jsPdfWillDrawCellCallback } from '../../../components/generics/helper';
import { Loader } from '../../../components/generics/Loader';
import { REPORT_TABLE_PAGINATION_ROWS_OPTIONS, EXPORT_PER_REQUEST_RECORD_LIMIT, REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED } from "../../../constants/commonConstants";
import { StyledTableRow, StyledTableCell } from "../../../components/Table/TableStyle";
import CommonTableHeaderRow from '../../../components/Table/CommonTableHeaderRow';
import CommonTablePagination from '../../../components/Table/CommonTablePagination';
import excelExport from '../../../services/exports/export-excel';

const nowrapCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};
const PurchaseOrderReportList = (props) => {
    const { purchaseOrderReportFilters, loadData, setLoadData } = props
    const { enqueueSnackbar } = useSnackbar();

    const userToken = useSelector((state) => state.login.userToken);
    const { selectedRolesRights = [], adminType = 0 } = useSelector((state) => state.login?.userData);
    const userData = useSelector((state) => state.login.userData);

    const [isPdfLoading, setIsPdfLoading] = useState(false);
    const [isPdfLoaded, setIsPdfLoaded] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [perPageRecords, setPerPageRecords] = useState(REPORT_TABLE_PAGINATION_ROWS_OPTIONS_SELECTED);

    const getPORSearchParams = (filters, page = 1, perPage = EXPORT_PER_REQUEST_RECORD_LIMIT) => {
        let PORSearchParams = {}

        if (filters?.searchBillBranchIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOBillBranchIds: filters.searchBillBranchIds };
        }
        if (filters?.searchShipBranchIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOShipBranchIds: filters.searchShipBranchIds };
        }
        if (filters?.searchCategoryIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOItemCategoryIds: filters.searchCategoryIds };
        }
        if (filters?.searchSubCategoryIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOItemSubCategoryIds: filters.searchSubCategoryIds };
        }
        if (filters?.searchBrandIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOItemBrandIds: filters.searchBrandIds };
        }
        if (filters?.searchItemIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOItemIds: filters.searchItemIds };
        }
        if (filters?.searchVendorIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchVendorIds: filters.searchVendorIds };
        }
        if (filters?.searchPOCreatedByIds !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOCreatedByIds: filters.searchPOCreatedByIds };
        }
        if (filters?.searchPOStatus !== '') {
            PORSearchParams = { ...PORSearchParams, searchPOStatusIds: filters.searchPOStatus };
        }

        PORSearchParams.searchPOFromDate = filters.fromDate;
        PORSearchParams.searchPOToDate = filters.toDate;
        PORSearchParams.pageNo = page;
        PORSearchParams.perPageRecords = perPage;
        PORSearchParams.formattedDate = "Y";
        return PORSearchParams;
    };

    const generatePurchaseOrderData = async (page = 0, rowsPerPage = perPageRecords) => {
        setIsLoading(true);
        try {
            const diffDays = moment(purchaseOrderReportFilters.toDate).diff(moment(purchaseOrderReportFilters.fromDate), 'days');
            if (diffDays > 90) {
                enqueueSnackbar('Date range cannot exceed 90 days', { variant: 'error' });
                setIsLoading(false);
                return;
            }
            const PORSearchParams = getPORSearchParams(purchaseOrderReportFilters, page + 1, rowsPerPage);

            const response = await fetchApi('reports/getPurchaseOrderReportData', PORSearchParams, userToken);
            setIsLoading(false);

            if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                if (response.data.poReportData.length > 0) {
                    enqueueSnackbar('Purchase Order Report Data Loaded Successfully', { variant: 'success', key: Date.now() });
                    setTableData(response?.data?.poReportData);
                    setTotalRecords(response.data.totalRecords);
                } else {
                    enqueueSnackbar('Ooops! No Record Found', { variant: 'error' });
                    setTableData([]);
                }
            } else {
                enqueueSnackbar(response.errorMessage, { variant: 'error' });
                setTableData([]);
                setTotalRecords(0);
            }
        } catch (err) {
            let message = 'Purchase Order Report List Loading Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            setTableData([])
            setTotalRecords(0);
            return { data: [], totalRecords: 0 };
        } finally {
            setLoadData(false);
        }
    }
    const exportAllPurchaseOrderReportData = async () => {
        const allPORData = [];
        try {
            const PORSearchParams = getPORSearchParams(purchaseOrderReportFilters);
            const totalPages = Math.ceil(totalRecords / PORSearchParams.perPageRecords);
            for (let page = 1; page <= totalPages; page++) {
                const pageParams = { ...PORSearchParams, pageNo: page };
                const response = await fetchApi('reports/getPurchaseOrderReportData', pageParams, userToken);
                if (response.status === 'Ok' && response.data.responseStatus === 'Ok') {
                    allPORData.push(...response.data.poReportData);
                }
            }
            return allPORData;
        } catch (err) {
            let message = 'Purchase Order Report List Export Failed';
            if (!err.message) {
                err.message = message
            }
            enqueueSnackbar(err.message, { variant: 'error' });
            return [];
        }
    }

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
        generatePurchaseOrderData(newPage, perPageRecords);
    };
    const handleChangeRowsPerPage = (event) => {
        setPerPageRecords(parseInt(event.target.value, 10));
        setPageNo(0);
        generatePurchaseOrderData(0, parseInt(event.target.value, 10));
    };
    const columnDefs = [
        { headerName: 'Po Number', pdfHead: 'Po No. ', field: 'poNo', xlCol: { dataType: 'string', dataFormatter: null, width: 16 }, headerSx: { width: "150px", textAlign: "left", whiteSpace: 'nowrap' }, allowSorting: false },
        { headerName: 'Po Date', pdfHead: 'Po Date', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, field: 'poDate', headerSx: { width: '130px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Bill To Branch', pdfHead: 'Bill Branch', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, field: 'billToBranchName', headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Ship To Branch', pdfHead: 'Ship Branch', xlCol: { dataType: 'string', dataFormatter: null, width: 40 }, field: 'shipToBranchName', headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Vendor Name', pdfHead: 'Vendor', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, field: 'vendorName', headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Po Created By', pdfHead: 'Po By', xlCol: { dataType: 'string', dataFormatter: null, width: 17 }, field: 'poCreatedByName', headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Item Name', pdfHead: 'Item Name', xlCol: { dataType: 'string', dataFormatter: null, width: 30 }, field: 'itemName', headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Part No.', pdfHead: 'Part No. ', field: 'partNo', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Brand', pdfHead: 'Brand', field: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Category', pdfHead: 'Category', field: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Subcategory', pdfHead: 'Sub cat', field: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 20 }, headerSx: { width: '150px', textAlign: "left" }, allowSorting: false },
        { headerName: 'UOM', pdfHead: 'UOM', field: 'unitTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Item QTY', pdfHead: 'Item QTY', field: 'qty', xlCol: { dataType: 'quantity', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Received QTY', pdfHead: 'Rec QTY', field: 'receivedQty', xlCol: { dataType: 'quantity', dataFormatter: null, width: 15 }, headerSx: { width: '103px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Balance QTY', pdfHead: 'Bal QTY', field: 'balanceQty', xlCol: { dataType: 'quantity', dataFormatter: null, width: 15 }, headerSx: { width: '100px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Net Amount', pdfHead: 'Net Amt', field: 'grossAmount', xlCol: { dataType: 'currency', dataFormatter: null, width: 15 }, headerSx: { width: '120px', textAlign: "left" }, allowSorting: false },
        { headerName: 'Landing Price', pdfHead: 'Landing Price', field: 'landingPrice', xlCol: { dataType: 'currency', dataFormatter: null, width: 20 }, headerSx: { width: '123px', textAlign: "left" }, allowSorting: false },
        { headerName: 'PO Status', pdfHead: 'PO Status', field: 'statusTitle', xlCol: { dataType: 'string', dataFormatter: null, width: 15 }, headerSx: { width: '123px', textAlign: "left" }, allowSorting: false },
    ];

    const getFilterSectionData = () => {
        return {
            filterHeaderXlStyle,
            filterHeaderXlDataStyle,
            filterData: [
                {
                    columns: [
                        { header: 'From Date', dataKey: 'fromDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'To Date', dataKey: 'toDate', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'BillBranch', dataKey: 'billToBranchName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'ShipBranch', dataKey: 'shipToBranchName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Vendor', dataKey: 'vendorName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Po Created By', dataKey: 'poCreatedByName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 31, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 31, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 59, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 59, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 53, halign: 'left', fontSize: 10 },
                        5: { cellWidth: 54, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            fromDate: moment(purchaseOrderReportFilters.fromDate).format('DD-MMM-YY'),
                            toDate: moment(purchaseOrderReportFilters.toDate).format('DD-MMM-YY'),
                            billToBranchName: purchaseOrderReportFilters.searchBillBranchIds === "" ? 'All Branches' : purchaseOrderReportFilters.searchBillBranchIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchBillBranchIds.split(',').length} ]` : purchaseOrderReportFilters?.searchBillBranchNames[0],
                            shipToBranchName: purchaseOrderReportFilters.searchShipBranchIds === "" ? 'All Branches' : purchaseOrderReportFilters.searchShipBranchIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchShipBranchIds.split(',').length} ]` : purchaseOrderReportFilters?.searchShipBranchNames[0],
                            vendorName: purchaseOrderReportFilters.searchVendorIds === "" ? 'All Vendors' : purchaseOrderReportFilters.searchVendorIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchVendorIds.split(',').length} ]` : purchaseOrderReportFilters?.searchVendorNames[0],
                            poCreatedByName: purchaseOrderReportFilters.searchPOCreatedByIds === "" ? 'All Users' : purchaseOrderReportFilters.searchPOCreatedByIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchPOCreatedByIds.split(',').length} ]` : (purchaseOrderReportFilters?.searchPOCreatedByNames || [])[0],
                        }
                    ]
                },
                {
                    columns: [
                        { header: 'Items', dataKey: 'itemName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Category', dataKey: 'categoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 1 } },
                        { header: 'Subcategory', dataKey: 'subCategoryTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'Brand', dataKey: 'brandName', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },
                        { header: 'PO Status', dataKey: 'statusTitle', xlCol: { dataType: 'string', dataFormatter: null, colSpan: 2 } },

                    ],
                    pdfColumnStyles: {
                        0: { cellWidth: 62, halign: 'left', fontSize: 10 },
                        1: { cellWidth: 59, halign: 'left', fontSize: 10 },
                        2: { cellWidth: 59, halign: 'left', fontSize: 10 },
                        3: { cellWidth: 53, halign: 'left', fontSize: 10 },
                        4: { cellWidth: 54, halign: 'left', fontSize: 10 },
                    },
                    data: [
                        {
                            itemName: purchaseOrderReportFilters.searchItemIds === "" ? 'All Items' : purchaseOrderReportFilters.searchItemIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchItemIds.split(',').length} ]` : purchaseOrderReportFilters?.searchItemNames[0],
                            categoryTitle: purchaseOrderReportFilters.searchCategoryIds === "" ? 'All Categories' : purchaseOrderReportFilters.searchCategoryIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchCategoryIds.split(',').length} ]` : (purchaseOrderReportFilters?.searchCategoryNames || [])[0],
                            subCategoryTitle: purchaseOrderReportFilters.searchSubCategoryIds === "" ? 'All Sub Categories' : purchaseOrderReportFilters.searchSubCategoryIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchSubCategoryIds.split(',').length} ]` : (purchaseOrderReportFilters?.searchSubCategoryNames || [])[0],
                            brandName: purchaseOrderReportFilters.searchBrandIds === "" ? 'All Brands' : purchaseOrderReportFilters.searchBrandIds.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchBrandIds.split(',').length} ]` : (purchaseOrderReportFilters?.searchBrandNames || [])[0],
                            statusTitle: purchaseOrderReportFilters.searchPOStatus === "" ? 'All Status' : purchaseOrderReportFilters.searchPOStatus.split(',').length > 1 ? `Multiple [ ${purchaseOrderReportFilters.searchPOStatus.split(',').length} ]` : (purchaseOrderReportFilters?.searchPOStatusTitle || [])[0],
                        }
                    ]
                },
            ]
        }
    }

    const printReport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            setIsPdfLoaded(false);
            const pdfColumns = columnDefs.map((col) => {
                return { header: col.pdfHead, dataKey: col.field, xlCol: col.xlCol }
            });

            const allData = await exportAllPurchaseOrderReportData();
            const fullData = allData.filter(row => row.isDataRow);

            jsPDF.API.events.push(["addFonts", callAddFontNirmalaBold]);
            jsPDF.API.events.push(["addFonts", callAddFontNirmalaNormal]);
            const doc = new jsPDF({
                orientation: 'l',
                format: 'a4',
                compress: true,
            });
            var totalPagesExp = '{total_pages_count_string}';
            doc.autoTableSetDefaults({
                headStyles: { fontSize: 6, halign: 'left', fillColor: [30, 89, 167] },
            });
            doc.setProperties({
                title: `purchaseOrderReport-${moment().format('DD-MMM-YY-HH-mm')}.pdf`,
                subject: "Purchase Order Report Report",
                author: "Maven Tech Labs Pvt. Ltd.",
                keywords: "maven tech, Maintenance system, purchase order",
                creator: "MTLPL",
            });
            autoTable(doc, {
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[0].columns,
                body: filterSectionData.filterData[0].data,
                columnStyles: filterSectionData.filterData[0].pdfColumnStyles,
                theme: 'grid',
            });
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY,
                margin: { top: 16, left: 5, right: 5, bottom: 5 },
                columns: filterSectionData.filterData[1].columns,
                body: filterSectionData.filterData[1].data,
                columnStyles: filterSectionData.filterData[1].pdfColumnStyles,
                theme: 'grid',
            });
          
            doc.line(0, doc.lastAutoTable.finalY + 2, doc.getPageWidth(), doc.lastAutoTable.finalY + 2);    
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 4,
                tableWidth: 'auto',
                margin: { top: 2, left: 5, right: 5, bottom: 5 },
                alternateRowStyles: {
                    fillColor: [255, 255, 255]
                },
                columns: pdfColumns,
                body: fullData,
                columnStyles: {
                    0: { minCellWidth: 20 },
                    1: { cellWidth: 16 },
                    2: { minCellWidth: 25 },
                    3: { minCellWidth: 25 },
                    4: { minCellWidth: 25 },
                    5: { minCellWidth: 15 },
                    6: { minCellWidth: 28, overflow: 'linebreak' },
                    7: { cellWidth: 11 },
                    8: { minCellWidth: 12 },
                    9: { minCellWidth: 16 },
                    10: { minCellWidth: 16 },
                    11: { minCellWidth: 9 },
                    12: { minCellWidth: 10, halign: 'right' },
                    13: { minCellWidth: 10, halign: 'right' },
                    14: { minCellWidth: 10, halign: 'right' },
                    15: { minCellWidth: 13, halign: 'right' },
                    16: { minCellWidth: 12, halign: 'right' },
                    17: { minCellWidth: 14 },
                },
                theme: 'grid',
                bodyStyles: {
                    fontSize: 6,
                    halign: 'left',
                },
                didParseCell: (data) => {
                    jsPdfDidParseCellCallback(data, pdfColumns,);
                },
                willDrawCell: (data) => {
                    jsPdfWillDrawCellCallback(data);
                },
                didDrawPage: function (data) {
                    data.settings.margin.top = 16;
                    const reportTitle = `Maintenance System | Purchase Order Report`;
                    jsPdfHeaderFooter(data, reportTitle, userData, totalPagesExp);
                },
            },
            );
            if (typeof doc.putTotalPages === 'function') {
                doc.putTotalPages(totalPagesExp)
            }
            setIsPdfLoaded(true);
            setTimeout(() => {
                // document.getElementById('output').data = doc.output('datauristring', `purchaseOrderSummary_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.pdf`);
                const blob = doc.output('blob');
                const url = URL.createObjectURL(blob);
                document.getElementById('output').data = url;
                setIsPdfLoading(false);
            }, 1000);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
            setIsPdfLoaded(false);
        }
    }

    const reportExcelExport = async () => {
        try {
            const filterSectionData = getFilterSectionData();
            setIsPdfLoading(true);
            const xlColumns = columnDefs.map((col) => {
                return { colHeader: col.headerName, field: col.field, xlCol: col.xlCol }
            });
            const reportTitle = `Maintenance System | Purchase Order Report`
            const xlConfig = {
                ...excelConfig,
                columns: xlColumns,
                fileName: `Purchase_Order_Report_${moment(new Date()).format("DD_MMM_YYYY_ddd_HH_mm")}.xlsx`,
                showFilterSection: true,
                filterSectionData,
                workSheetTitle: `Purchase_Order_Report`,
                headerFooter: {
                    firstHeader: `&L&14&K104FA2 ${reportTitle} &R&B&12 Company Name: ${userData.companyName}`,
                    firstFooter: `&L&12 Report By : ${userData.userFullName}  [ ${userData.branchName} ] &C&12 Report Time :  ${moment(new Date()).format("DD MMM YYYY, ddd, HH:mm")} &R&12 Page: &P of &N`
                },
                sheetHeaderData: [{
                    "caption": "Report Name :",
                    "value": `Purchase Order Report`,
                    "colSpan": 4
                }, {
                    "caption": "Company Name :",
                    "value": userData.companyName,
                    "colSpan": 6
                }],
            };
            const allData = await exportAllPurchaseOrderReportData();
            const fullData = allData.filter(row => row.isDataRow);
            await excelExport.processExcel(xlConfig, fullData);
            setIsPdfLoading(false);
        } catch (err) {
            console.log(err);
            setIsPdfLoading(false);
        }
    }

    const backToScreenView = () => {
        setIsPdfLoaded(false);
    }

    useEffect(() => {
        if (loadData) {
            generatePurchaseOrderData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    return (
        <>
            <React.Fragment key="por">
                {isLoading && <div style={{ width: "100%", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                {tableData.length > 0 &&
                    <React.Fragment key="por_dataBlock">
                        <div className='report--header--container'>
                            <div key="clss_report_name" className='report--for--heading'>Purchase Order Report</div>
                            <div key="por_report_action_container" className="report-export-button--container">
                                {!isPdfLoaded && <React.Fragment key="por_Actions">
                                    {hasAccess(selectedRolesRights, '4092', adminType) && <div key="por_xls_btn" className="dashed--btn" onClick={() => reportExcelExport()}> EXPORT AS EXCEL </div>}
                                    {hasAccess(selectedRolesRights, '4093', adminType) && <div key="por_pdf_btn" className="dashed--btn" onClick={() => printReport()}> EXPORT AS PDF </div>}
                                    {hasAccess(selectedRolesRights, '4091', adminType) && <div key="por_prn_btn" className="dashed--btn" onClick={() => printReport()}> PRINT </div>}
                                </React.Fragment>}
                                {isPdfLoaded && <React.Fragment key="por_backBtn"><div key="por_back_btn" className="dashed--btn" onClick={() => backToScreenView()}> Back </div> </React.Fragment>}
                                {isPdfLoading && <div key="por_loading_container" style={{ width: "100px", textAlign: 'center', marginTop: '10px' }}><Loader /></div>}
                            </div>
                        </div>
                        {!isPdfLoaded && <React.Fragment key="por_dataTable">
                            <div className="table--container">
                                <TableContainer component={Paper} className="jc--scroll" style={{ maxHeight: '85vh' }}>
                                    <Table sx={{ minWidth: 1300 }} stickyHeader aria-label="sticky table">
                                        <CommonTableHeaderRow columnDefs={columnDefs} />
                                        <TableBody>
                                            {tableData && tableData.length ? tableData.map((row, index) => (
                                                row.isDataRow ?
                                                    <StyledTableRow key={`${row.rowType}-D-${index}`} className="grid">
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.poNo}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.poDate}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.billToBranchName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.shipToBranchName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.vendorName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.poCreatedByName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.itemName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.partNo || ''}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.brandName}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.categoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.subCategoryTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.unitTitle}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{NumberFormatter(row.qty, 3)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{NumberFormatter(row.receivedQty, 3)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{NumberFormatter(row.balanceQty, 3)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{NumberFormatter(row.grossAmount, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{NumberFormatter(row.landingPrice, 2)}</StyledTableCell>
                                                        <StyledTableCell style={{ ...nowrapCellStyle }}>{row.statusTitle}</StyledTableCell>
                                                    </StyledTableRow>
                                                    : null
                                                // row.isTotalRow &&
                                                // <StyledTableRow key={`${row.rowType}-T-${index}`} style={{ background: "#e4ebf5" }} className="grid">
                                                //     <StyledTableCell colSpan={row.colSpan} style={{ width: "50px", textAlign: "left", fontWeight: "700" }}>{row.totalCaption}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "100px", textAlign: "right" }}>{row.qty > 0 ? NumberFormatter(row.qty, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "103px", textAlign: "right" }}>{row.receivedQty > 0 ? NumberFormatter(row.receivedQty, 2) : ' '}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "100px", textAlign: "right" }}>{NumberFormatter(row.balanceQty, 2)}</StyledTableCell>
                                                //     <StyledTableCell style={{ width: "120px", textAlign: "right", fontWeight: "700" }}>{NumberFormatter(row.grossAmount, 2)}</StyledTableCell>
                                                // </StyledTableRow>
                                            )) :
                                                <StyledTableRow key={`$por_no_records_row`}>
                                                    <StyledTableCell style={{ width: "100%" }} colSpan={columnDefs.length} align="center">
                                                        <div key="por_no_records" className='no--records'> No Records </div>
                                                    </StyledTableCell>
                                                </StyledTableRow>}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                            <div className="report-pagination--container">
                                <CommonTablePagination
                                    rowsPerPageOption={REPORT_TABLE_PAGINATION_ROWS_OPTIONS}
                                    count={totalRecords}
                                    rowsPerPage={perPageRecords}
                                    className=""
                                    page={pageNo}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </div>
                        </React.Fragment>}
                        {isPdfLoaded && <object id="output" type="application/pdf" width="100%" height="600" title="MTLPL - Maintenance System - Purchase Order Report" aria-label="MTLPL - Maintenance System - Purchase Order Report"></object>}
                    </React.Fragment>
                }
            </React.Fragment>
        </>
    );
}
export default PurchaseOrderReportList;